<template>
    <div class="settlement-page settlement-page-root">
        <div class="settlement-container">
            <SettlementHeader
                v-if="site && !loading && !needOnboarding"
                :site="site"
                :ym="ym"
                :energy-source="energySource"
                :loading="loading"
                :matrix-status="matrixStatus"
                :household-count="householdCount"
                :main-tab="mainTab"
                @update:ym="ym = $event"
                @update:energySource="energySource = $event"
                @update:mainTab="mainTab = $event"
                @open-run="showRunDrawer = true"
                @open-site="showSiteModal = true"
                @refresh="onHeaderRefresh"
            />

            <div class="stl-body">
                <div v-if="loading" class="stl-loading" style="padding: 80px">
                    <b-spinner variant="primary" />
                    <span>단지 프로필 확인 중...</span>
                </div>

                <SettlementWizard v-else-if="needOnboarding" @complete="onWizardComplete" />

                <SettlementDashboard
                    v-else-if="site && mainTab === 'dashboard'"
                    ref="dashboard"
                    :key="`dash-${siteKey}`"
                    :site="site"
                    :ym="ym"
                    :energy-source="energySource"
                    @open-run="showRunDrawer = true"
                    @open-site="showSiteModal = true"
                    @matrix-status="onMatrixStatus"
                    @household-count="onHouseholdCount"
                />

                <SettlementLedgerTab
                    v-else-if="site && mainTab === 'ledger'"
                    :site="site"
                    :energy-source="energySource"
                    @go-tab="onLedgerGoTab"
                    @open-site-settings="showSiteModal = true"
                />

                <div v-else class="stl-empty" style="padding: 80px">
                    <i class="fa fa-exclamation-circle"></i>
                    <p>{{ loadError || "단지 프로필을 불러오지 못했습니다." }}</p>
                    <button type="button" class="stl-btn stl-btn-primary" style="margin-top: 12px" @click="reload">
                        다시 시도
                    </button>
                </div>
            </div>

            <SettlementRunDrawer
                v-if="site"
                v-model="showRunDrawer"
                :site="site"
                :ym="ym"
                :energy-source="energySource"
                @settled="onSettled"
                @open-site-settings="showSiteModal = true"
            />

            <b-modal
                v-model="showSiteModal"
                size="xl"
                hide-footer
                hide-header
                body-class="stl-site-modal-body"
                content-class="stl-site-modal-content"
            >
                <div class="stl-site-modal-shell">
                    <div class="settlement-page">
                        <div class="stl-modal-head">
                            <span class="t">단지정보</span>
                            <span class="stl-chip plain">단지 · 세대 설정</span>
                            <span class="sp"></span>
                            <button type="button" class="stl-modal-x" @click="showSiteModal = false">×</button>
                        </div>
                        <SettlementSiteTab v-if="site && showSiteModal" :site="site" @site-updated="onSiteUpdated" />
                    </div>
                </div>
            </b-modal>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import SettlementWizard from "./SettlementWizard.vue";
import SettlementSiteTab from "./tabs/SettlementSiteTab.vue";
import SettlementHeader from "./components/SettlementHeader.vue";
import SettlementDashboard from "./components/SettlementDashboard.vue";
import SettlementRunDrawer from "./components/SettlementRunDrawer.vue";
import SettlementLedgerTab from "./tabs/SettlementLedgerTab.vue";
import { parseApiError, shiftRegYm, currentRegYm, regYmToYm } from "./utils/settlementFormat.js";

export default {
    name: "SettlementMgmt",
    components: {
        SettlementWizard,
        SettlementSiteTab,
        SettlementHeader,
        SettlementDashboard,
        SettlementRunDrawer,
        SettlementLedgerTab,
    },
    data() {
        const prevYm = shiftRegYm(currentRegYm(), -1);
        return {
            site: null,
            needOnboarding: false,
            loading: false,
            loadError: null,
            siteKey: 0,
            showSiteModal: false,
            showRunDrawer: false,
            ym: regYmToYm(prevYm),
            energySource: "ALL",
            matrixStatus: null,
            householdCount: 0,
            mainTab: "dashboard",
        };
    },
    mounted() {
        this.reload();
    },
    methods: {
        async reload() {
            this.loading = true;
            this.loadError = null;
            this.needOnboarding = false;
            try {
                const { data } = await backEndApi.settlement.getSite();
                this.site = data;
                this.siteKey += 1;
                this.applyEnergySourceFallback(this.site);
            } catch (err) {
                const e = parseApiError(err);
                if (e.status === 404 || e.errcode === "SITE_NOT_FOUND") {
                    this.site = null;
                    this.needOnboarding = true;
                } else {
                    this.site = null;
                    this.loadError = e.message;
                }
            } finally {
                this.loading = false;
                if (this.site && this.mainTab === "dashboard") {
                    this.$nextTick(() => this.$refs.dashboard?.refresh?.());
                }
            }
        },
        async onWizardComplete() {
            await this.reload();
            this.showRunDrawer = true;
        },
        onSiteUpdated(site) {
            this.site = site;
            this.siteKey += 1;
            this.applyEnergySourceFallback(site);
        },
        applyEnergySourceFallback(site) {
            if (!site) return;
            const enabled = [];
            if (site.elecEnabled === "Y") enabled.push("Elec");
            if (site.gasEnabled === "Y") enabled.push("Gas");
            if (site.waterEnabled === "Y") enabled.push("Water");
            if (!enabled.length) return;
            if (enabled.length === 1) {
                this.energySource = enabled[0];
                return;
            }
            if (!["ALL", ...enabled].includes(this.energySource)) {
                this.energySource = "ALL";
            }
        },
        onSettled() {
            this.$refs.dashboard?.refresh?.();
        },
        onLedgerGoTab(tab) {
            if (tab === "run") this.showRunDrawer = true;
        },
        /** dashboard가 matrix 로드 후에만 emit — 로딩 중 null/0으로 헤더를 덮지 않음 */
        onMatrixStatus(status) {
            this.matrixStatus = status;
        },
        onHouseholdCount(count) {
            this.householdCount = Number(count) || 0;
        },
        async onHeaderRefresh() {
            await this.reload();
            if (this.site && this.mainTab === "dashboard") {
                await this.$refs.dashboard?.refresh?.();
            }
        },
    },
};
</script>

<style lang="scss" src="./styles/settlement.scss"></style>

<style lang="scss" scoped>
.settlement-page-root {
    height: 91vh;
    overflow: hidden;
}
</style>

<style lang="scss">
.stl-site-modal-content {
    border-radius: 14px;
    border: none;
    box-shadow: 0 24px 64px -12px rgba(20, 26, 40, 0.5);
    overflow: hidden;
}

.stl-site-modal-body {
    padding: 0;
    overflow: hidden;
}

.stl-site-modal-shell {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 80px);
    max-height: 900px;
    min-height: 480px;
    overflow: hidden;
    background: #fff;

    > .settlement-page {
        flex: 1;
        min-height: 0;
        height: 100%;
        max-height: none;
        overflow: hidden;
    }
}

.stl-site-modal-shell .stl-modal-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 15px 22px;
    border-bottom: 1px solid #e6e9ef;
    flex-shrink: 0;
    background: #fff;
    z-index: 20;

    .t {
        font-size: 15px;
        font-weight: 700;
        white-space: nowrap;
    }

    .sp {
        flex: 1;
    }

    .stl-modal-x {
        width: 28px;
        height: 28px;
        border-radius: 7px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #7e8798;
        font-size: 15px;
        border: 1px solid #e6e9ef;
        background: #fff;
        cursor: pointer;

        &:hover {
            background: #f7f8fb;
        }
    }
}
</style>
